/**
 * Стоящая заявка на кромке: покупка выигравшей стороны по фиксированной цене.
 *
 * # Зачем
 *
 * На пятиминутном окне наступает момент, когда рынок уже РАЗРЕШЁН по существу,
 * но ещё не рассчитан: выигравшая сторона котируется 0.98–0.99, проигравшая
 * 0.01–0.02. В этот момент есть повторяемая сделка — встать заявкой по 0.99 и
 * ждать того, кто набирает проигравшую сторону по 0.01. Биржа сводит две
 * покупки дополняющих исходов, когда сумма их цен достигает единицы (MINT), так
 * что наша заявка и есть контрагент, которого он ищет. Мы при этом мейкер:
 * комиссии не платим, а до погашения в 1.00 остаётся цент.
 *
 * # Почему отдельный модуль, а не третий режим в lib/quick.ts
 *
 * Тот файл предупреждает прямым текстом: набор режимов цены нельзя расширять в
 * одиночку, потому что каждый режим связан с лесенкой пред-подписи трейдера.
 * Здесь цена ФИКСИРОВАНА и к лесенке отношения не имеет — она попадает в
 * пред-подпись ровно тогда, когда оказывается в пределах PRESIGN_LEVELS шагов
 * от вершины, то есть в целевом состоянии рынка попадает сама собой.
 *
 * Модуль чистый — ни сети, ни React, ни времени изнутри, — потому что здесь
 * решается, куда уйдут настоящие деньги.
 */

import type { Market, PlaceOrderRequest, Token } from "../api/types";
import { best, type AssetBook } from "../store/book";
import { free, known } from "./fees";
import { cmp, format, parse } from "./money";
import { bookProblem } from "./quick";
import { sharesForUsd } from "./size";

/**
 * Цена заявки.
 *
 * Константа, а не настройка, и это сознательно. Смысл у числа ровно один: это
 * последний шаг сетки перед единицей, на котором сделка ещё приносит деньги, и
 * выбирается он не вкусом оператора, а устройством рынка. Настройкой она стала
 * бы полем, в которое однажды впишут 0.9 — и кнопка, обещающая цент, начнёт
 * покупать риск на десять.
 *
 * Кратность шагу рынка при этом всё равно проверяется: у этих рынков шаг
 * плавает между окнами (наблюдались и 0.01, и 0.001), 0.99 кратна обоим, но
 * шаг 0.1 сделал бы её недопустимой, и молча подставить соседнюю цену нельзя.
 */
export const MAKER_PRICE = "0.99";

/**
 * Порог, ниже которого рынок разрешённым не считается.
 *
 * Сравнения двух бидов между собой НЕ ХВАТАЕТ, и это стоило бы денег: на
 * неразрешённом окне (0.55 против 0.45) сравнение тоже называет победителя, и
 * кнопка предложила бы купить его по 0.99 — то есть заплатить 44 цента за
 * исход, который рынок оценивает в 55. Страховкой это не закрыто: единственная
 * страховка ниже — предупреждение о перекрытии, а оно молчит, когда у
 * выигравшей стороны пуста сторона продажи (обычное состояние в конце окна).
 *
 * Число выбрано по смыслу сделки: заявка по 0.99 — это «до погашения остался
 * цент», и осмысленна она там, где рынок сам уже котирует победителя у единицы.
 * 0.97 держит премию к рынку в пределах двух центов — то есть покупает не
 * дороже, чем вдвое от того, что сделка обещает заработать.
 *
 * # Это же число задано у трейдера, и совпадать они обязаны
 *
 * У авторежима порог свой (AUTOMAKER_RESOLVED_BID, умолчание то же), потому что
 * подсистема работает с закрытым браузером и константу отсюда прочитать не
 * может. Разойдись они — оператор увидел бы горящую кнопку там, где бот молчит.
 * Действующее число трейдера видно в GET /status нотой automaker_bid, и панель
 * авторежима подписана ИМ, а не этой константой: расхождение обязано быть
 * видно, а не подразумеваться.
 */
export const RESOLVED_BID = "0.97";

/** Исход, на который встаёт заявка, либо причина, по которой его не выбрать. */
export type Winning = { ok: true; assetId: string; outcome: string } | { ok: false; reason: string };

/**
 * Имя исхода — с той же подстановкой, что во всём остальном интерфейсе.
 *
 * Пустое `outcome` из Gamma встречается, и подставляют вместо него номер и
 * OrderForm, и OrdersPanel, и лесенка. Здесь это не косметика: имя попадает на
 * кнопку и в эхо отправленной команды, а эхо — единственная запись о том, на
 * какой из двух исходов ушли деньги.
 */
function outcomeName(token: Token): string {
  return token.outcome || `исход ${token.outcome_index}`;
}

/**
 * Кто впереди — по ЛУЧШЕЙ ПОКУПКЕ каждой стороны.
 *
 * Именно bid, а не мидпоинт и не последняя сделка. Бид — это цена, по которой
 * рынок готов купить ПРЯМО СЕЙЧАС, то есть единственная из трёх, за которой
 * стоят живые деньги. Мидпоинт на пустой стороне выродится, а последняя сделка
 * описывает прошлое: на развороте она называет сторону, которая только что
 * перестала быть выигравшей.
 *
 * Равенство — это отказ, а не «берём первый». Ситуация «оба по 0.50» означает,
 * что рынок не разрешён вовсе, а заявка по 0.99 имеет смысл ровно тогда, когда
 * он разрешён; молчаливый выбор здесь превратил бы кнопку в подбрасывание
 * монеты за деньги.
 *
 * Сравнение при этом ОБСТАВЛЕНО двумя абсолютными условиями, и оба про деньги:
 * лидер обязан стоять не ниже RESOLVED_BID, а вторая сторона — не выше
 * 1 − RESOLVED_BID. Первое отсекает неразрешённый рынок, второе — стакан,
 * которого не бывает: сумма двух лучших покупок больше единицы означает
 * забытую или отставшую заявку, и выбирать по ней исход, на который уйдёт вся
 * ставка, нельзя (арбитраж свёл бы такую пару мгновенно).
 *
 * Оба условия про ВТОРУЮ сторону, и когда покупок у неё нет, они молчат оба.
 * Тогда лидера подтверждает её ПРОДАЖА: она обязана стоять не выше
 * 1 − RESOLVED_BID. Без этого единственный уцелевший бид выбирал бы сторону в
 * одиночку — а на перевороте в конце окна это бид проигравшего.
 */
export function winningSide(
  tokens: readonly Token[],
  books: Readonly<Record<string, AssetBook | undefined>>,
): Winning {
  if (tokens.length < 2) {
    return { ok: false, reason: "у рынка меньше двух исходов — выбирать не из чего" };
  }

  const floor = parse(RESOLVED_BID);
  if (floor === null) return { ok: false, reason: `порог ${RESOLVED_BID} не разобран` };

  // Пустая сторона покупки — это «не кандидат», а не ноль. Считать её нулём
  // нельзя ровно по прежней причине: пустой стакан бывает и у выигравшей
  // стороны, — но и ронять из-за неё весь выбор больше не надо: победителя
  // теперь называет абсолютный порог, а не только сравнение.
  //
  // Продажа второй стороны хранится рядом с её покупкой: когда покупок у неё
  // нет, единственная оставшаяся проверка лидера — это цена, по которой ЕЁ саму
  // готовы отдать. См. ниже.
  const ranked: { assetId: string; outcome: string; bid: number | null; ask: number | null }[] = [];

  for (const token of tokens) {
    const name = outcomeName(token);
    const book = books[token.asset_id];
    // Причина берётся у соседнего модуля дословно: два описания одного
    // состояния, сказанные по-разному, заставляют оператора гадать, разные ли
    // это состояния.
    const problem = bookProblem(book);
    if (problem !== null) return { ok: false, reason: `${name}: ${problem}` };

    // Рассинхронизированный стакан отсекается ЗДЕСЬ, а не в bookProblem: там он
    // законно проходит, потому что соседние кнопки берут из стакана цену,
    // которую оператор видит на лесенке своими глазами. Тут по стакану
    // выбирается СТОРОНА, и делает это код: разомкнутая цепочка seq означает,
    // что часть уровней могла остаться от прошлого состояния рынка, а
    // отставший бид проигравшей стороны — это вся ставка на неверный исход.
    if (book!.status === "syncing") {
      return {
        ok: false,
        reason:
          `${name}: стакан рассинхронизирован (пропущено обновлений: ${book!.missed}) — ` +
          `уровни могут быть неверны, сторону по ним не выбрать`,
      };
    }

    ranked.push({
      assetId: token.asset_id,
      outcome: name,
      bid: best(book!.bids)?.p ?? null,
      ask: best(book!.asks)?.p ?? null,
    });
  }

  ranked.sort((a, b) => (b.bid ?? -1) - (a.bid ?? -1));
  const leader = ranked[0]!;
  const second = ranked[1]!;

  if (leader.bid === null) {
    return { ok: false, reason: "заявок на покупку нет ни на одной стороне — сравнивать не с чем" };
  }
  if (second.bid !== null && second.bid === leader.bid) {
    return {
      ok: false,
      reason: `стороны стоят одинаково (${format(leader.bid)}) — рынок не разрешён, ставить нечего`,
    };
  }
  if (leader.bid < floor) {
    return {
      ok: false,
      reason:
        `${leader.outcome} впереди, но стоит ${format(leader.bid)} — ниже ${RESOLVED_BID}: ` +
        `рынок ещё не разрешён, и заявка по ${MAKER_PRICE} была бы ставкой, а не кромкой`,
    };
  }
  // 1 000 000 — единица в микро-единицах money; тот же литерал, что в takerLimit.
  if (second.bid !== null && second.bid + leader.bid > 1_000_000) {
    return {
      ok: false,
      reason:
        `обе стороны котируются высоко (${leader.outcome} ${format(leader.bid)}, ` +
        `${second.outcome} ${format(second.bid)}) — сумма покупок больше единицы, ` +
        `такого стакана не бывает: выбирать сторону не по чему`,
    };
  }
  // Второй стороне НЕЧЕМ подтвердить лидера, если у неё нет покупок: обе
  // проверки выше — и равенство, и сумма — на ней и стоят, а порог проверяет
  // только сам бид лидера. А один бид врёт: на перевороте в конце окна
  // проигравшая сторона какое-то время держит забытую заявку на 0.96, пока у
  // выигравшей покупки уже сняли. Тогда лидером становится ПРОИГРАВШИЙ, обе
  // проверки молчат, предупреждения о перекрытии нет (пустой стакан продажи), и
  // вся ставка уходит по 0.99 на исход, который погасится в ноль.
  //
  // Свидетель в этом случае один — цена, по которой вторую сторону готовы
  // ОТДАТЬ: продажа Down по 0.02 означает, что её оценивают в два цента, то есть
  // Up и правда стоит около единицы. Дорогая продажа второй стороны либо её
  // отсутствие означает, что подтвердить лидера нечем.
  if (second.bid === null) {
    const ceiling = 1_000_000 - floor;
    if (second.ask === null || second.ask > ceiling) {
      return {
        ok: false,
        reason:
          `у стороны «${second.outcome}» нет ни одной покупки, а её продажа ` +
          `${second.ask === null ? "отсутствует" : format(second.ask)} — ` +
          `подтвердить, что впереди ${leader.outcome} (${format(leader.bid)}), нечем: ` +
          `одна заявка на другой стороне бывает и забытой`,
      };
    }
  }
  return { ok: true, assetId: leader.assetId, outcome: leader.outcome };
}

export interface MakerContext {
  market: Market;
  tokens: readonly Token[];
  books: Readonly<Record<string, AssetBook | undefined>>;
  /** Активная сумма панели в долларах. null — не выбрана или не разобрана. */
  usd: string | null;
}

export type MakerResolved =
  | {
      ok: true;
      req: PlaceOrderRequest;
      /** Исход, на который встаёт заявка, — для подписи кнопки и эха. */
      outcome: string;
      price: string;
      /** Сумма, которую нажал оператор. */
      usd: string;
      /** Размер в share — то, что уйдёт на биржу. */
      size: string;
      /**
       * Цена встречной продажи, если заявка её ДОТЯНУЛАСЬ. null — стакан не
       * перекрыт и заявка встанет.
       *
       * Не отказ: это предупреждение. Запрет — дело трейдера и биржи, и их
       * мотивированный отказ оператор обязан увидеть, а не получить погасшую
       * кнопку, которая молчит о причине.
       */
      crossing: string | null;
      /**
       * Цена встречной ПОКУПКИ ДОПОЛНЯЮЩЕГО исхода, с которой биржа сведёт нашу
       * заявку типом MINT. null — такой заявки нет.
       *
       * Отдельно от crossing, потому что это другое место стакана и другие
       * слова для оператора: перекрывает нас не продавец нашего токена, а
       * покупатель соседнего, и цена у него своя (0.01 против нашей 0.99).
       */
      mint: string | null;
      /** Комиссия мейкера: "0" или null, когда расписание рынка незнакомо. */
      fee: string | null;
    }
  | { ok: false; reason: string };

/**
 * Собирает команду. Отказ означает «подставить нечего», а не «нельзя».
 */
export function resolveMaker(ctx: MakerContext): MakerResolved {
  const side = winningSide(ctx.tokens, ctx.books);
  if (!side.ok) return { ok: false, reason: side.reason };

  const book = ctx.books[side.assetId]!;
  // Шаг берётся из СТАКАНА, а не из справочника: tick_size_change доходит до
  // стакана раньше, и по устаревшему шагу мы отвергали бы верную цену и
  // пропускали неверную. То же правило, что в OrderForm и resolveQuick.
  const tickSize = book.tickSize || ctx.market.tick_size;
  const tick = parse(tickSize);
  const price = parse(MAKER_PRICE);
  if (tick === null || tick <= 0 || price === null) {
    return { ok: false, reason: `шаг цены «${tickSize}» не разобран` };
  }
  if (price % tick !== 0) {
    return {
      ok: false,
      reason: `цена ${MAKER_PRICE} не кратна шагу ${tickSize} — биржа отклонит такой ордер целиком`,
    };
  }

  if (ctx.usd === null || ctx.usd === "") return { ok: false, reason: "не выбран размер" };
  const size = sharesForUsd(ctx.usd, MAKER_PRICE);
  if (size === null) {
    return {
      ok: false,
      reason: `суммы $${ctx.usd} не хватает даже на 0.01 share по цене ${MAKER_PRICE}`,
    };
  }

  // Минимум рынка проверяется по РАЗМЕРУ, а не по сумме: он задан в share, и
  // одна и та же сумма проходит на одном рынке и не проходит на другом. На
  // btc-updown это ровно про пресеты мельче $5: минимум там 5 share, то есть
  // $4.95 по нашей цене.
  const min = parse(ctx.market.min_order_size);
  const got = parse(size);
  if (min !== null && min > 0 && got !== null && cmp(got, min) < 0) {
    return {
      ok: false,
      reason:
        `$${ctx.usd} по цене ${MAKER_PRICE} даёт ${size} share — ` +
        `меньше минимального размера рынка (${format(min)})`,
    };
  }

  const req: PlaceOrderRequest = {
    condition_id: ctx.market.condition_id,
    asset_id: side.assetId,
    side: "BUY",
    price: MAKER_PRICE,
    size,
    // GTC, а не GTD: заявка должна дожить до конца окна, а окно кончается само.
    order_type: "GTC",
    // Ради чего всё и затевалось: биржа отвергнет ордер, который перекрыл бы
    // стакан, вместо того чтобы исполнить его тейкером.
    post_only: true,
  };

  return {
    ok: true,
    req,
    outcome: side.outcome,
    price: MAKER_PRICE,
    usd: ctx.usd,
    size,
    crossing: crossingAsk(book, price),
    mint: mintCounter(ctx, side.assetId, price),
    fee: makerFee(ctx.market),
  };
}

/**
 * Цена встречной продажи, до которой дотягивается наша заявка. null — не
 * дотягивается.
 *
 * Смотрит только на СВОЙ стакан; встречные покупки дополняющего исхода —
 * отдельным правилом (mintCounter), потому что живут они в другом стакане и
 * называются оператору другими словами.
 */
function crossingAsk(book: AssetBook, price: number): string | null {
  const ask = best(book.asks);
  if (ask === null) return null;
  return ask.p <= price ? format(ask.p) : null;
}

/**
 * Встречная ПОКУПКА дополняющего исхода, с которой биржа сведёт нашу заявку.
 *
 * Биржа матчит две покупки дополняющих исходов типом MINT, как только сумма их
 * цен достигает единицы (ctf-exchange-v2, Trading.sol): наша покупка Up по 0.99
 * сходится с чужой покупкой Down по 0.01. Это и есть сделка, ради которой
 * кнопка сделана, — но post_only отличает СТОЯЩУЮ заявку от немедленно
 * сводимой: чужая покупка, УЖЕ стоящая в стакане на момент постановки, делает
 * тейкером нас, и такой ордер биржа отвергает целиком.
 *
 * Раньше это состояние здесь не считалось вовсе («полную картину знает только
 * биржа»), и предупреждения не было ровно в том состоянии рынка, ради которого
 * кнопка существует. Считать его есть чем: стакан соседнего токена уже загружен
 * — по нему выбиралась сторона строкой выше.
 *
 * Только для двустороннего рынка: MINT — это про пару дополняющих исходов, и на
 * рынке с тремя токенами «дополняющего» просто нет.
 */
function mintCounter(ctx: MakerContext, assetId: string, price: number): string | null {
  if (ctx.tokens.length !== 2) return null;
  const other = ctx.tokens.find((t) => t.asset_id !== assetId);
  if (!other) return null;
  const bid = best(ctx.books[other.asset_id]?.bids ?? []);
  if (bid === null) return null;
  // 1 000 000 — единица в микро-единицах money.
  return bid.p + price >= 1_000_000 ? format(bid.p) : null;
}

/**
 * Комиссия этой заявки: ноль у мейкера.
 *
 * Ноль — это ЗНАНИЕ, а не его отсутствие, и получено оно так же, как число на
 * соседних кнопках: из расписания рынка. Расписание незнакомо — прочерк, потому
 * что «бесплатно» это утверждение о деньгах, и брать его с потолка нельзя
 * (см. lib/quick.ts, resolveFee).
 */
function makerFee(market: Market): string | null {
  if (!known(market.fees) && !free(market.fees)) return null;
  return "0";
}

/**
 * Отвергла ли биржа заявку за перекрытие стакана.
 *
 * Это НЕ поломка и не отказ по существу: post-only ордер, дотянувшийся до
 * встречной заявки, гасится по определению — ордер не создан, деньги не
 * потрачены, позиция не изменилась. Единственное верное действие оператора —
 * посмотреть на новую цену и решить заново.
 *
 * Красный цвет в панели зарезервирован за тем, что требует разбирательства
 * (отказ риск-гейта, неверные суммы, 401). Покрасив им штатный исход, мы приучим
 * не читать красное — и настоящий отказ пройдёт мимо глаз. Тот же довод, по
 * которому заведён isNoMatch для FAK.
 *
 * Сверяется по подстроке, потому что кода у биржи нет: 400 с текстом
 * «invalid post-only order: order crosses book» — это всё, что она сообщает.
 * Наш собственный риск-гейт отвечает своими словами, поэтому проверяются оба.
 */
export function isPostOnlyCross(error: string): boolean {
  return error.includes("post-only order") || error.includes("не встанет в стакан");
}
